import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { SingleValue } from 'react-select';

import { getPaginatedCompaniesNames } from '@/services/ws-permissions/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useModalState } from '@/js/libs/hooks/useModalState';
import { useSubscribe } from '@/js/libs/observable';
import { AsyncSelect } from '@/js/libs/ui/src/V2';
import { useLazyRequest } from '@/js/libs/use-request';

import { DEV_MODE } from 'src/constants';
import type { Option } from 'src/types';
import { VerifyPinModal } from './containers/VerifyPinModal';
import type { CompanyIdentifiersFragment } from './context';
import { useCompanySwitchContext } from './context';
import SelectContainer from './styles';

const CompaniesSwitch: React.FC = () => {
  const getPaginatedCompaniesNamesRequest = useLazyRequest({
    apiMethod: getPaginatedCompaniesNames,
  });

  const [t] = useTranslation(['common']);
  const { company, onCompanyChange } = useCompanySwitchContext();

  const pinModalState = useModalState<CompanyIdentifiersFragment>();

  const loadOptions = (prefix: string) =>
    getPaginatedCompaniesNamesRequest.load({ filter: prefix }).then(({ data }) =>
      data.items.map(
        (item): Option<CompanyIdentifiersFragment> => ({
          label: item.name,
          value: {
            id: item.id,
            companyUUID: item.companyUUID,
            companyRole: item.companyRole,
            name: item.name,
            temporaryAccessRequired: item.temporaryAccessRequired,
          },
        }),
      ),
    );
  const pending = useSubscribe(getPaginatedCompaniesNamesRequest.pending$);

  const handleChange = useCallbackRef((option: SingleValue<Option<CompanyIdentifiersFragment>>) => {
    if (!company && !option) {
      return;
    }
    if (DEV_MODE && option?.value.temporaryAccessRequired) {
      pinModalState.open(option.value);
      return;
    }
    onCompanyChange(option?.value ?? null);
  });

  const handlePinConfirmed = useCallbackRef(() => {
    if (!pinModalState.isMounted) {
      return;
    }
    onCompanyChange(pinModalState.payload);
    pinModalState.close();
  });

  return (
    <SelectContainer data-testid='companies-switch'>
      <AsyncSelect<Option<CompanyIdentifiersFragment>>
        onChange={handleChange}
        placeholder={t('common:SELECT_COMPANY')}
        defaultOptions
        loadOptions={loadOptions}
        isClearable
        isLoading={pending}
        value={
          company
            ? {
                label: company.name,
                value: company,
              }
            : null
        }
      />
      {pinModalState.isMounted && (
        <VerifyPinModal
          state={pinModalState}
          companyId={pinModalState.payload.id}
          companyName={pinModalState.payload.name}
          onConfirmed={handlePinConfirmed}
        />
      )}
    </SelectContainer>
  );
};

export default CompaniesSwitch;
